import { selectorFamily } from "recoil";

import {
  GROUPING_GRID_NO_GROUPS,
  ID_SEARCH_PATTERN_TEXT_PREFIX,
} from "../../constants";
import { IExtendableDto } from "../../dto";
import { StarredDisplayModeEnum } from "../../enums";
import {
  getTableFilteredGroupedRowsFromMap,
  Log,
  parseSpecificSearchStringToArray,
  resolveIsFilteredRows,
  transformFilterStoreObjectToCriteria,
  unique,
} from "../../helpers";
import { DtoFieldType, ITypeormWhereCriteria, RydeUiUnit } from "../../models";
import {
  nearbyFilteringRideIdAtom,
  starredDisplayModeAtomFamily,
  tableFiltersSchemaAtomFamily,
  tableGroupingAF,
  tableSearchAF,
} from "../atoms";
import { boundedSelectorFamily } from "../selector-factories";
import { controlsSF } from "./dynamic-schema.selectors";
import {
  tableFilteredAllGroupsRowsIdsSF,
  tableFilteredGroupedRowsMapSF,
  tableGroupingFieldResolvedSF,
  tableGroupValuesSF,
} from "./table-layout.selectors";

export const filterCriteriaSF = selectorFamily<
  ITypeormWhereCriteria<IExtendableDto> | undefined,
  RydeUiUnit
>({
  key: "filterCriteriaSF",
  get:
    entity =>
    ({ get }) => {
      const filterSchema = get(tableFiltersSchemaAtomFamily(entity));

      if (!filterSchema) {
        return undefined;
      }

      const controls = get(controlsSF(entity));
      const filterCriteria = transformFilterStoreObjectToCriteria(
        filterSchema,
        controls,
      );

      Log.debug(`Criteria object for ${entity}`, filterCriteria);
      return filterCriteria;
    },
});

export const isFilteredRowsSF = boundedSelectorFamily<boolean, RydeUiUnit>({
  key: "isFilteredRowsSF",
  get:
    entity =>
    ({ get }) => {
      const groupValues = get(tableGroupValuesSF(entity));
      const columnGrouping = get(tableGroupingAF(entity));
      const groupingField = columnGrouping[0];
      const filteredRowIds = get(tableFilteredAllGroupsRowsIdsSF(entity));
      const groupedRowsMap = get(tableFilteredGroupedRowsMapSF(entity));

      return resolveIsFilteredRows({
        groupValues,
        groupingField,
        filteredRowIdsSize: filteredRowIds.size,
        groupedRowsMap,
      });
    },
});

export const searchBoxHasSearchableValueSF = selectorFamily<
  boolean,
  RydeUiUnit
>({
  key: "searchBoxHasSearchableValueSF",
  get:
    entity =>
    ({ get }) => {
      const searchBoxValue = get(tableSearchAF(entity));

      if (
        !searchBoxValue
          ?.toLowerCase()
          ?.startsWith(ID_SEARCH_PATTERN_TEXT_PREFIX)
      ) {
        return false;
      }

      const searchValueIds = parseSpecificSearchStringToArray(searchBoxValue);

      if (searchValueIds.filter(Boolean).length) {
        return true;
      }

      return false;
    },
});

export const areFiltersPausedByDisplayModeSF = selectorFamily<
  boolean,
  RydeUiUnit
>({
  key: "areFiltersPausedByDisplayModeSF",
  get:
    entity =>
    ({ get }) => {
      const starredDisplayMode = get(starredDisplayModeAtomFamily(entity));

      if (starredDisplayMode === StarredDisplayModeEnum.FiltersIgnored) {
        return true;
      }

      if (entity === "ride") {
        const filteringRideId = get(nearbyFilteringRideIdAtom);

        if (filteringRideId) {
          return true;
        }
      }

      return false;
    },
});

export const areFiltersPausedSF = selectorFamily<boolean, RydeUiUnit>({
  key: "areFiltersPausedSF",
  get:
    entity =>
    ({ get }) => {
      if (get(areFiltersPausedByDisplayModeSF(entity))) {
        return true;
      }

      return get(searchBoxHasSearchableValueSF(entity));
    },
});

export const isSearchboxPausedSF = selectorFamily<boolean, RydeUiUnit>({
  key: "isSearchboxPausedSF",
  get:
    entity =>
    ({ get }) => {
      const starredDisplayMode = get(starredDisplayModeAtomFamily(entity));

      if (starredDisplayMode === StarredDisplayModeEnum.FiltersIgnored) {
        return true;
      }

      if (entity === "ride") {
        const filteringRideId = get(nearbyFilteringRideIdAtom);

        if (filteringRideId) {
          return true;
        }
      }

      return false;
    },
});

export const isSearchboxFilteringIgnoresDatesSF = selectorFamily<
  boolean,
  RydeUiUnit
>({
  key: "isSearchboxFilteringIgnoresDatesSF",
  get:
    entity =>
    ({ get }) => {
      const starredDisplayMode = get(starredDisplayModeAtomFamily(entity));

      if (starredDisplayMode === StarredDisplayModeEnum.FiltersIgnored) {
        return true;
      }

      if (entity === "ride") {
        const filteringRideId = get(nearbyFilteringRideIdAtom);

        if (filteringRideId) {
          return false;
        }
      }

      const isSearchboxWithValue = get(searchBoxHasSearchableValueSF(entity));

      if (isSearchboxWithValue) {
        return true;
      }

      return false;
    },
});

export const groupWithFilteredRowsSF = boundedSelectorFamily<
  string[],
  RydeUiUnit
>({
  key: "groupWithFilteredRowsSF",
  get:
    entity =>
    ({ get }) => {
      const groupValues = get(tableGroupValuesSF(entity));
      const groupingFieldNameResolved = get(
        tableGroupingFieldResolvedSF(entity),
      );

      if (!Array.isArray(groupValues)) {
        return [GROUPING_GRID_NO_GROUPS];
      }

      const groupedRowsMap = get(tableFilteredGroupedRowsMapSF(entity));

      const groupsWithRows = groupValues.filter(groupValue => {
        const groupedRows = getTableFilteredGroupedRowsFromMap({
          groupedRowsMap,
          groupingFieldValue: groupValue,
          groupingFieldNameResolved,
        });

        return groupedRows.length > 0;
      });

      if (!groupsWithRows.length) {
        return [GROUPING_GRID_NO_GROUPS];
      }

      return groupsWithRows;
    },
});

/** All row ids currently shown in the grid (every non-empty group), for header “select all”. */
export const tableVisibleRowIdsForSelectAllSF = boundedSelectorFamily<
  number[],
  RydeUiUnit
>({
  key: "tableVisibleRowIdsForSelectAllSF",
  get:
    entity =>
    ({ get }) => {
      const groupedRowsMap = get(tableFilteredGroupedRowsMapSF(entity));
      const groupValues = get(groupWithFilteredRowsSF(entity));
      const groupingFieldNameResolved = get(
        tableGroupingFieldResolvedSF(entity),
      );
      const allIds: number[] = [];

      for (const groupValue of groupValues) {
        const rows = getTableFilteredGroupedRowsFromMap({
          groupedRowsMap,
          groupingFieldValue: groupValue as
            DtoFieldType | typeof GROUPING_GRID_NO_GROUPS,
          groupingFieldNameResolved,
        });

        for (const row of rows) {
          allIds.push(row.id);
        }
      }

      return unique(allIds);
    },
});
